import { ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { defineStore } from 'pinia'
import { useTimeoutFn, useStorage } from '@vueuse/core'
import { useUserStore } from '@/stores/user'
import { useVoiceStore } from '@/stores/voice'
import { useSnackbarStore } from '@/stores/snackbar'
import { usePopupStore } from '@/stores/popup'
import { useDraftsStore } from '@/stores/drafts'
import { useEditorStore } from '@/stores/editor'
import { useTranscriptionStore } from '@/stores/transcription'
import { http, HTTPError } from '@/helpers/http'
import { track } from '@/helpers/mixpanelDes'
import HtmlCleaner from '@/helpers/HtmlCleaner'
import { proposeChapterTitle as proposeTitle, planChapterInsertions, introTitle } from '@/helpers/audiobookChapters'
import { Speaker } from '@/types/voice'
import { Node } from '@tiptap/pm/model'
import type { TChapterProposal } from '@/types/audiobookChapters'
import type { TUserSortOptions } from '@/types/projects'
import type { TCombinedSearchResult } from '@/types/search'
import type {
  TAudiobookDraftResponse,
  TAudiobookCreateRes,
  TAudiobookPreview,
  TProjectAudiobook,
  TAudiobookResponse,
  TAudiobookGenerateResponse,
  TAudiobookSectionGenerateResponse,
  TAudiobookExport,
  TAudiobookExportOptions,
  TAudiobookExportStatusResponse,
  TAudiobookExportQueueResult,
  TAudiobookCoverUploadResponse,
  TChapterPreviewResponse,
  TChapterRetryResponse,
  TAudiobookChapter,
} from '@/types/audiobooks'
import type { Editor } from '@tiptap/core'

export const useAudiobooksStore = defineStore('audiobooks', () => {
  const user = useUserStore()
  const route = useRoute()
  const voices = useVoiceStore()
  const snack = useSnackbarStore()
  const popup = usePopupStore()
  const router = useRouter()

  const audiobooks = ref<TProjectAudiobook[]>([])
  const sortOptions = ref<TUserSortOptions>({})
  const draft = ref<TAudiobookDraftResponse | null>(null)
  const project = ref<TAudiobookResponse | null>(null)
  const player = new Audio()
  const playedNode = ref<Node | null>(null)
  const isPlaying = ref<boolean>(false)
  const editPanelVisible = ref(false)
  const showChapters = useStorage<boolean>('audiobook-show-chapters', true)
  const markingChapters = ref<boolean>(false)
  const activePanelTab = ref<'speakers' | 'chapters'>('speakers')
  const saveDraftNow = ref<(() => Promise<boolean>) | null>(null)
  const creditsLeft = ref<number | null>(null)
  const loaded = ref<boolean>(false)
  let pendingLoad: Promise<void> | null = null

  function invalidate(): void {
    loaded.value = false
  }

  function errorBody<T>(error: unknown): Partial<T> | undefined {
    if (!(error instanceof HTTPError)) return undefined
    try {
      return JSON.parse(error.response) as Partial<T>
    } catch {
      return undefined
    }
  }

  async function fetchAudiobooks(query?: string) {
    sortOptions.value = useStorage('sortOptions', {} as TUserSortOptions).value
    const data: TProjectAudiobook[] | null = await http.post<TProjectAudiobook[] | null>('/tts/projects', {
      query: query ?? '',
    })
    if (!data) {
      throw new Error(`Failed to fetch audiobooks`)
    }
    audiobooks.value = data
    // a filtered result is not the full list, so it must not satisfy ensureAudiobooks()
    loaded.value = !query

    sort(sortOptions.value[user.user.id]?.projects || 'newest')
  }

  async function ensureAudiobooks(): Promise<void> {
    if (loaded.value) {
      return
    }
    if (pendingLoad) {
      return pendingLoad
    }

    pendingLoad = fetchAudiobooks()
    try {
      await pendingLoad
    } finally {
      pendingLoad = null
    }
  }

  function onAudiobooksTab(): boolean {
    return route.name === 'projects' && route.params.type === 'audiobooks'
  }

  const { start: schedulePoll, stop: cancelPoll } = useTimeoutFn(pollAudiobooks, 15e3, { immediate: false })

  function checkAudiobooks(): void {
    if (!onAudiobooksTab()) {
      return
    }

    cancelPoll()
    schedulePoll()
  }

  async function pollAudiobooks(): Promise<void> {
    if (!onAudiobooksTab()) {
      return
    }

    if (audiobooks.value.some(isInProgress)) {
      try {
        await fetchAudiobooks()
      } catch {
        // a failed poll must not stop the polling
      }
    }

    schedulePoll()
  }

  async function deleteAudiobook(project: TProjectAudiobook) {
    const index = audiobooks.value.findIndex((item) => {
      return project.id === item.id
    })
    if (index >= 0) {
      track('audiobooks-delete', { id: project.id })
      audiobooks.value.splice(index, 1)
      const res = await fetch(`/tts/delete_audiobook_version`, {
        method: 'POST',
        body: JSON.stringify({
          book_id: project.id,
        }),
      })
      if (!res.ok) {
        throw new Error(`Failed to delete audiobook`)
      }
    }
  }

  async function renameAudiobook(book_id: number, bookName: string) {
    track('audiobooks-rename', { id: book_id })
    const res = await fetch('/tts/rename_audiobook', {
      method: 'POST',
      body: JSON.stringify({
        book_id,
        bookName,
      }),
    })
    if (!res.ok) {
      throw new Error(`Failed to rename project`)
    }
    const data = await res.json()
    return data
  }

  async function getAudiobookPreviewUrl(audiobook: TProjectAudiobook): Promise<string>
  async function getAudiobookPreviewUrl(id: number, token: string): Promise<string>
  async function getAudiobookPreviewUrl(arg1: TProjectAudiobook | number, arg2?: string): Promise<string> {
    const firstArgNotNumber = typeof arg1 !== 'number'
    const audiobookId = firstArgNotNumber && arg1.id ? arg1.id : arg1
    const token = firstArgNotNumber && arg1.token ? arg1.token : arg2

    const res = await fetch(`/tts/preview/create_entry_token/${audiobookId}`, {
      method: 'POST',
    })
    if (!res.ok) {
      throw new Error(`Failed to rename project`)
    }

    const data = await res.json()
    const tokenToUse = token || data.token

    return `/tts/preview/${tokenToUse}/${data.id}/1/1/1`
  }

  function sort(sortBy: string) {
    switch (sortBy) {
      case 'az':
        audiobooks.value.sort((a, b) => a.name.localeCompare(b.name))
        break
      case 'za':
        audiobooks.value.sort((a, b) => b.name.localeCompare(a.name))
        break
      case 'oldest':
        audiobooks.value.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())
        break
      case 'newest':
        audiobooks.value.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
        break
    }
  }

  async function preview(projectId: number): Promise<TAudiobookPreview | null> {
    track('audiobooks-preview', { id: projectId })
    return await http.post<TAudiobookPreview>('/tts/preview/create_entry_token/' + projectId)
  }

  function isInProgress(project: TProjectAudiobook | TCombinedSearchResult): boolean {
    return project?.status === 1 || project?.status === 3
  }

  async function createDraft(
    content: string,
    articleTitle: string = '',
    author: string = '',
  ): Promise<TAudiobookCreateRes> {
    track('audiobooks-create-draft')
    if (!author) {
      const userStore = useUserStore()
      author = userStore.getUserFullName(userStore.user)
    }
    if (!articleTitle) {
      articleTitle = 'New Audiobook'
    }
    const res = await http.post<TAudiobookCreateRes>('/tts/created_draft', {
      content,
      articleTitle,
      author,
    })
    if (res.status !== 'error') {
      useDraftsStore().invalidate('audiobooks')
    }
    return res
  }

  async function createFromDraft(draftId: number): Promise<number | null> {
    const drafts = useDraftsStore()
    const editorStore = useEditorStore()
    const transcriptions = useTranscriptionStore()
    const draft = drafts.draft?.id === draftId ? drafts.draft : await drafts.get(draftId)

    if (!draft?.id) {
      await popup.alert('The manuscript could not be loaded')

      return null
    }

    const rawContent = transcriptions.draftToProjectContent(draft, editorStore.getEditor())

    const container = HtmlCleaner.createNodeFromHtml(`<div>${rawContent}</div>`)[0] as HTMLElement
    HtmlCleaner.cleanupHtmlForAudioBook(container)

    const res = await createDraft(container.outerHTML, draft.name ?? '')

    if (res.status === 'error') {
      await popup.alert(res.message)
      return null
    }

    track('audiobook-created-from-draft', { draftId })
    return res.draftId
  }

  async function getAudiobookDraft(id: number, signal?: AbortSignal) {
    draft.value = null
    try {
      draft.value = await http.post<TAudiobookDraftResponse>('/get_audiobook_draft', { id }, false, 0, signal)
      creditsLeft.value = draft.value.creditsLeft
    } catch (error) {
      if (signal?.aborted) throw error
      console.error(error)
    }
  }

  async function getAudiobook(draft_id: number, signal?: AbortSignal) {
    project.value = null
    try {
      project.value = await http.post<TAudiobookResponse>('/drafts/getAudioBook', { draft_id }, false, 0, signal)
    } catch (error) {
      if (signal?.aborted) throw error
      console.error(error)
    }
  }

  function contentToDraft(html: string): string {
    const speakersData = voices.documentSpeakers.map(({ name, pitch, speed, style, customName }) => ({
      name,
      pitch,
      speed,
      style,
      customName,
    }))
    const speakersComment = `<!--ab-speakers:${JSON.stringify(speakersData)}-->`
    return `<div
      id="tts-content-editor"
      class="tts-voice"
      data-tts-voice="${voices.mainSpeaker?.name}"
      data-tts-pitch="${voices.mainSpeaker?.pitch}"
      data-tts-speed="${voices.mainSpeaker?.speed}"
      data-tts-style="${voices.mainSpeaker?.style}"
      data-tts-name="${voices.mainSpeaker?.displayName}"
      ${voices.mainSpeaker?.customName ? `data-tts-custom-name="${voices.mainSpeaker.customName}"` : ''}
    >${speakersComment}${html}</div>`
  }

  function draftToContent(html: string): string {
    const parser = new DOMParser()
    const doc = parser.parseFromString(html, 'text/html')

    const div = doc.body.firstElementChild
    const detectedLanguage = draft.value?.detected_language ?? null

    if (!div) {
      voices.mainSpeaker = voices.speakerForLanguage(detectedLanguage)
      return ''
    }

    const commentNode = Array.from(div.childNodes).find(
      (n) => n.nodeType === 8 && (n.nodeValue ?? '').startsWith('ab-speakers:'),
    )
    if (commentNode) {
      try {
        voices.savedSpeakers = JSON.parse((commentNode.nodeValue ?? '').slice('ab-speakers:'.length))
      } catch {
        voices.savedSpeakers = []
      }
      commentNode.remove()
    } else {
      voices.savedSpeakers = []
    }

    if (voices.mainSpeaker) {
      return div.innerHTML
    }

    const attributes: Record<string, string> = {}
    for (const attr of div.attributes) {
      if (attr.name.startsWith('data-tts-')) {
        const key = attr.name.replace('data-tts-', '')
        attributes[key] = attr.value
      }
    }

    if (attributes.voice) {
      const { voice, style = 'Default', speed = 0, pitch = 0, customName } = attributes
      const voiceTTS = voices.findByName(voice)

      if (!voiceTTS) {
        voices.mainSpeaker = voices.speakerForLanguage(detectedLanguage)
      } else {
        voices.mainSpeaker = new Speaker(voice, ~~pitch, ~~speed, style, customName || null, voiceTTS)
      }
    }

    if (!voices.mainSpeaker) {
      voices.mainSpeaker = voices.speakerForLanguage(detectedLanguage)
    }

    return div.innerHTML
  }

  function escapeXML(str: string): string {
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&apos;')
  }

  function generateParagraphSSML(node: Node): string {
    let ssml = ''

    const speaker = voices.getSpeakerFromNode(node) || voices.mainSpeaker!
    const voice = speaker.name ?? 'en-US-JennyNeural'
    const speed = `${speaker.speed ?? 0}%`
    const pitch = `${speaker.pitch ?? 0}%`
    const style = speaker.style ?? 'Default'

    let content = ''
    node.content.forEach((mark) => {
      if (mark.type.name === 'audiobookBreak') {
        content += generateBreakSSML(mark)
      } else if (mark.text) {
        content += escapeXML(mark.text)
      }
    })

    ssml += `<voice name="${voice}">`

    if (style) {
      ssml += `<mstts:express-as style="${style}">`
    }

    ssml += `<prosody rate="${speed}" pitch="${pitch}">`

    if (node.attrs.level && node.attrs.level > 0) {
      const headerTag = `h${node.attrs.level}`
      ssml += `<${headerTag}><emphasis level="moderate">${content}</emphasis></${headerTag}>`
    } else {
      ssml += `<p>${content}</p>`
    }

    ssml += `</prosody>`

    if (style) {
      ssml += `</mstts:express-as>`
    }

    ssml += `</voice>`

    return ssml
  }

  function generateBreakSSML(node: Node): string {
    const breakValue = node.attrs['data-tts-break'] || 'medium'
    return `<break strength="${breakValue}" />`
  }

  /**
   * A chapter marker travels to the backend as a comment the synthesis engine never sees;
   * the backend cuts the book into one file per chapter at these comments.
   */
  function generateChapterSSML(node: Node): string {
    return `<!--ab-chapter:${encodeURIComponent(String(node.attrs.title ?? ''))}-->`
  }

  function hasTextBeforeFirstChapter(editor: Editor): boolean {
    let seenText = false
    let decided = false
    editor.state.doc.forEach((child) => {
      if (decided) return
      if (child.type.name === 'audiobookChapter') {
        decided = true
        return
      }
      if (child.textContent.trim() !== '') seenText = true
    })

    return seenText
  }

  function countChapters(editor: Editor): number {
    let markers = 0
    editor.state.doc.forEach((child) => {
      if (child.type.name === 'audiobookChapter') markers++
    })
    if (!markers) return 1

    return markers + (hasTextBeforeFirstChapter(editor) ? 1 : 0)
  }

  function proposeChapterTitle(text: string): string {
    return proposeTitle(text)
  }

  /**
   * Inserts one or more chapter breaks. When the document has none yet and
   * the first of them does not already sit at the very start, a real,
   * editable "Introduction" marker is inserted at position 0 too (QA-011),
   * so chapter 1 is visible in the text and not panel-only.
   */
  function insertChapterBreaks(editor: Editor, requested: TChapterProposal[]): void {
    if (!requested.length) return

    let hasMarkers = false
    editor.state.doc.forEach((child) => {
      if (child.type.name === 'audiobookChapter') hasMarkers = true
    })
    const introNodeSize = editor.schema.nodes.audiobookChapter.create({ title: introTitle }).nodeSize
    const plan = planChapterInsertions(hasMarkers, introNodeSize, requested)

    const chain = editor.chain().focus()
    if (plan.introAt !== null) chain.insertAudiobookChapter(plan.introAt, introTitle)
    for (const proposal of [...plan.proposals].reverse()) {
      chain.insertAudiobookChapter(proposal.pos, proposal.title)
    }
    chain.run()
  }

  async function previewChapters(html: string): Promise<TChapterPreviewResponse | null> {
    try {
      const res = await http.post<TChapterPreviewResponse>('/tts/chapters/preview', { html })

      return res.result ? res : null
    } catch {
      return null
    }
  }

  async function retryChapterUpload(bookId: number): Promise<TChapterRetryResponse> {
    try {
      const res = await http.post<TChapterRetryResponse>('/tts/chapters/retry_upload', { book_id: bookId })
      track('audiobooks-chapters-retry-upload', { id: bookId, result: res.result })
      if (res.result) invalidate()

      return res
    } catch (error) {
      const body = errorBody<TChapterRetryResponse>(error)
      track('audiobooks-chapters-retry-upload', { id: bookId, result: false })

      return {
        result: false,
        chapters_state: body?.chapters_state ?? 'partial',
        uploaded: body?.uploaded,
        missing: body?.missing,
        message: body?.message ?? 'The upload could not be retried; try again in a moment.',
      }
    }
  }

  async function updateChapterTitles(bookId: number): Promise<TAudiobookChapter[] | null> {
    try {
      const res = await http.post<{ result: boolean; chapters: TAudiobookChapter[]; message?: string }>(
        '/tts/chapters/update_titles',
        { book_id: bookId },
      )

      return res.result ? res.chapters : null
    } catch {
      return null
    }
  }

  function generateParagraphTextForAi(node: Node): string {
    let content = ''

    node.content.forEach((mark) => {
      if (mark.type.name === 'audiobookBreak') {
        const breakValue = mark.attrs['data-tts-break'] || 'medium'
        content += `<break strength="${breakValue}" />`
      } else if (mark.text) {
        content += mark.text
      }
    })

    return content.trim()
  }

  function nodesToSSML(nodes: Node[]): string {
    let ssml =
      '<speak xmlns="http://www.w3.org/2001/10/synthesis" xmlns:mstts="http://www.w3.org/2001/mstts" xmlns:emo="http://www.w3.org/2009/10/emotionml" version="1.0" xml:lang="en-US">'

    nodes.forEach((node) => {
      if (node.type.name === 'audiobookParagraph') {
        ssml += generateParagraphSSML(node)
      } else if (node.type.name === 'audiobookBreak') {
        ssml += generateBreakSSML(node)
      } else if (node.type.name === 'audiobookChapter') {
        ssml += generateChapterSSML(node)
      }
    })

    ssml += '</speak>'

    return ssml
  }

  function contentToSSML(editor: Editor): string {
    const nodes: Node[] = []
    const children = editor.state.doc.content
    children.forEach((node: any) => {
      nodes.push(node)
    })

    return nodesToSSML(nodes)
  }

  function estimateCreditsUsage(ssml: string): number {
    const content = ssml
      .replace(/<!--ab-chapter:[^>]*?-->/g, '')
      .replace(/<speak[^>]*>/gi, '')
      .replace(/<\/speak>/gi, '')
      .replace(/<voice[^>]*>/gi, '')
      .replace(/<\/voice>/gi, '')
      .trim()

    return content.length
  }

  async function previewParagraph(ssml: string, draftId: number, node: Node) {
    player.pause()

    if (node.toString() === playedNode.value?.toString() && isPlaying.value) {
      isPlaying.value = false
      return
    }

    isPlaying.value = false
    playedNode.value = node

    try {
      const response = await http.post<TAudiobookSectionGenerateResponse>('/tts/sample', {
        ssml,
        draftId,
        oldHash: node.attrs.id,
      })

      if ('status' in response && response.status === 'error') {
        if (response.showOffer) {
          popup
            .confirm("You don't have enough credits. Would you like to buy more credits?", {
              labelConfirm: 'Buy credits',
            })
            .then((res) => {
              res && router.push('/profile/upgrade')
            })
        } else {
          snack.add(response.message)
        }

        return
      } else if ('url' in response) {
        player.oncanplay = () => {
          player.play()
          isPlaying.value = true
        }
        player.onended = () => {
          previewStop()
        }
        player.src = response.url
        creditsLeft.value = response.creditsLeft

        return response
      }
    } catch (error) {
      console.error(error)
      snack.add('Unable to generate audio, please try again in a moment')
    }
  }

  async function previewExternal(src: string, node: Node) {
    player.pause()

    if (node.toString() === playedNode.value?.toString() && isPlaying.value) {
      isPlaying.value = false
      return
    }

    isPlaying.value = false
    playedNode.value = node

    try {
      player.oncanplay = () => {
        player.play()
        isPlaying.value = true
      }

      player.onended = () => {
        previewStop()
      }
      player.src = src
    } catch (error) {
      console.error('Error playing external audio:', error)
      snack.add('Unable to play audio source')
      isPlaying.value = false
      playedNode.value = null
    }
  }

  function previewStop() {
    player.pause()
    player.oncanplay = null
    isPlaying.value = false
  }

  async function generateAudio(
    ssml: string,
    html: string,
    draftId: number,
    voices: { language: string; voicename: string }[],
    analytics: { chapters: number; chapters_source: 'manual' | 'none' } = { chapters: 1, chapters_source: 'none' },
  ) {
    const res = await http.post<TAudiobookGenerateResponse>('/tts/synthesizeLong', { draftId, voices, ssml, html })
    if (res.status === 'error') {
      track('audiobooks-generate', { status: res.status, error: res.message, ...analytics })
    } else {
      track('audiobooks-generate', { status: res.status, ...analytics })
      invalidate()
    }
    return res
  }

  async function queueAcxExport(bookId: number, options: TAudiobookExportOptions): Promise<TAudiobookExportQueueResult> {
    try {
      const res = await http.post<{ result: boolean; export: TAudiobookExport | null; error?: string }>(
        '/tts/export/acx/queue',
        { book_id: bookId, ...options },
      )
      track('audiobooks-export-acx', { id: bookId, status: res.export?.status ?? 'error' })

      return { export: res.result ? (res.export ?? null) : null }
    } catch (error) {
      const body = errorBody<TAudiobookExportQueueResult>(error)
      track('audiobooks-export-acx', { id: bookId, status: 'refused', reason: body?.reason ?? 'error' })

      return {
        export: null,
        reason: body?.reason ?? null,
        message: body?.message ?? 'Failed to queue the ACX export',
        needs_confirmation: body?.needs_confirmation ?? false,
      }
    }
  }

  async function uploadAcxCover(bookId: number, file: File): Promise<TAudiobookCoverUploadResponse> {
    const body = new FormData()
    body.append('book_id', String(bookId))
    body.append('cover', file)
    try {
      const response = await fetch('/tts/export/acx/cover', { method: 'POST', body })
      const data = (await response.json()) as TAudiobookCoverUploadResponse
      track('audiobooks-acx-cover-upload', { id: bookId, result: data.result })

      return data
    } catch {
      return { result: false, message: 'The cover could not be uploaded; try again in a moment.' }
    }
  }

  async function getAcxExportStatus(bookId: number): Promise<TAudiobookExportStatusResponse | null> {
    try {
      const res = await http.get<TAudiobookExportStatusResponse>(`/tts/export/acx/status/${bookId}`)

      return res.result ? res : null
    } catch {
      console.error('Failed to fetch audiobook ACX export status')

      return null
    }
  }

  async function queueM4bExport(bookId: number, options: TAudiobookExportOptions): Promise<TAudiobookExportQueueResult> {
    try {
      const res = await http.post<{ result: boolean; export: TAudiobookExport | null; error?: string }>(
        '/tts/export/m4b/queue',
        { book_id: bookId, title: options.title, author: options.author, narrator: options.narrator, cover: options.cover ?? '' },
      )
      track('audiobooks-export-m4b', { id: bookId, status: res.export?.status ?? 'error' })

      return { export: res.result ? (res.export ?? null) : null }
    } catch (error) {
      const body = errorBody<TAudiobookExportQueueResult>(error)
      track('audiobooks-export-m4b', { id: bookId, status: 'refused', reason: body?.reason ?? 'error' })

      return {
        export: null,
        reason: body?.reason ?? null,
        message: body?.message ?? 'Failed to queue the m4b export',
        needs_confirmation: body?.needs_confirmation ?? false,
      }
    }
  }

  async function getM4bExportStatus(bookId: number): Promise<TAudiobookExportStatusResponse | null> {
    try {
      const res = await http.get<TAudiobookExportStatusResponse>(`/tts/export/m4b/status/${bookId}`)

      return res.result ? res : null
    } catch {
      console.error('Failed to fetch audiobook m4b export status')

      return null
    }
  }

  async function getActiveExports(): Promise<TAudiobookExport[]> {
    try {
      const res = await http.get<{ result: boolean; exports: TAudiobookExport[] }>('/tts/exports/active')

      return res.result ? (res.exports ?? []) : []
    } catch {
      console.error('Failed to fetch active audiobook exports')

      return []
    }
  }

  function getErrorForAudioBookStatus(status: number): string {
    switch (status) {
      case 1:
      case 3: {
        return 'The audio book is still processing!'
      }
      case 4: {
        return 'There was an error processing this book!'
      }
      default: {
        return 'There is a problem with your book! Invalid status!'
      }
    }
  }

  return {
    createDraft,
    createFromDraft,
    audiobooks,
    draft,
    project,
    isPlaying,
    playedNode,
    editPanelVisible,
    showChapters,
    markingChapters,
    activePanelTab,
    saveDraftNow,
    creditsLeft,
    isInProgress,
    preview,
    fetchAudiobooks,
    ensureAudiobooks,
    invalidate,
    checkAudiobooks,
    deleteAudiobook,
    renameAudiobook,
    getAudiobookPreviewUrl,
    getAudiobook,
    getAudiobookDraft,
    sort,
    draftToContent,
    contentToDraft,
    contentToSSML,
    nodesToSSML,
    previewParagraph,
    previewStop,
    generateAudio,
    generateParagraphSSML,
    generateChapterSSML,
    generateParagraphTextForAi,
    hasTextBeforeFirstChapter,
    countChapters,
    proposeChapterTitle,
    insertChapterBreaks,
    previewChapters,
    retryChapterUpload,
    updateChapterTitles,
    uploadAcxCover,
    getErrorForAudioBookStatus,
    estimateCreditsUsage,
    previewExternal,
    queueAcxExport,
    getAcxExportStatus,
    queueM4bExport,
    getM4bExportStatus,
    getActiveExports,
  }
})
